import { useContext, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaFlag, FaBars, FaTimes, FaMoon, FaSun } from "react-icons/fa";
import logo from "../../../assets/mascot.png";

import { RegionContext } from "../../../context/RegionContext";
import { fetchCountry } from "../../../controllers/fetchCountry";
import { getRegions } from "../../../controllers/admin-pannel/regionController";
import AnnouncementBar from "./AnnouncementBar";
import SubscribeModal from "../subscribe/subscribeModal";

const Header = () => {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const { updateSelectedRegionId } = useContext(RegionContext);
  const [isSubscribeModal, setIsSubscribeModal] = useState(false);
  const [announcementBarData, setAnnouncementBarData] = useState(null);

  // Theme
  const [isDark, setIsDark] = useState(false);
  const applyTheme = (dark) => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("reelstheme", dark ? "dark" : "light");
  };
  useEffect(() => {
    const saved = localStorage.getItem("reelstheme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const startDark = saved === "dark" || (!saved && prefersDark);
    setIsDark(startDark);
    applyTheme(startDark);
  }, []);
  const toggleTheme = () =>
    setIsDark((prev) => {
      const next = !prev;
      applyTheme(next);
      return next;
    });

  // Regions
  useEffect(() => {
    (async () => {
      const list = await getRegions();
      const sorted = list.sort((a, b) => a.name.localeCompare(b.name));
      setRegions(sorted);

      const stored = localStorage.getItem("selectedRegionId");
      let region = sorted.find((r) => r.id === stored);
      if (!region) {
        const country = await fetchCountry();
        region =
          sorted.find(
            (r) => r.name.toLowerCase() === String(country).toLowerCase()
          ) || sorted.find((r) => r.isDefault);
      }
      if (region) {
        setSelectedRegion(region);
        updateSelectedRegionId(region.id);
        localStorage.setItem("selectedRegionId", region.id);
      }
    })();
  }, [updateSelectedRegionId]);

  const handleRegionSelect = (r) => {
    setSelectedRegion(r);
    updateSelectedRegionId(r.id);
    localStorage.setItem("selectedRegionId", r.id);
    setIsRegionOpen(false);
    setMenuOpen(false);
  };

  useEffect(() => {
    const announcementRegion = regions.find(
      (r) => r?.id === selectedRegion?.id
    );
    setAnnouncementBarData({
      detail: announcementRegion?.promotionDescription,
      message: announcementRegion?.promotionLabel,
      linkHref: announcementRegion?.promotionLink,
      linkLabel: announcementRegion?.promotionLabel,
    });
  }, [regions, selectedRegion?.id]);

  const navLinks = [
    { to: "/casinos", label: "Casinos" },
    { to: "/responsible-gambling", label: "Responsible Gambling" },
    { to: "/blogs", label: "Blogs" },
    { to: "/about", label: "About" },
  ];

  return (
    <header className="sticky top-0 z-[1000] bg-white dark:bg-[#12151f] text-gray-900 dark:text-gray-100 shadow-sm">
      {announcementBarData?.message && (
        <AnnouncementBar {...announcementBarData} />
      )}
      <SubscribeModal
        isOpen={isSubscribeModal}
        onClose={() => setIsSubscribeModal(false)}
      />

      <nav className="max-w-[1120px] mx-auto px-6 py-3 flex justify-between items-center">
        {/* Logo */}
        <motion.div
          className="flex items-center gap-3 cursor-pointer xl:-ml-28"
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          onClick={() => navigate("/")}
        >
          <img
            src={logo}
            alt="Logo"
            className="h-10 w-auto object-contain scale-110"
          />
          <span className="text-lg font-semibold">Mr Reel</span>
        </motion.div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 border border-gray-300 dark:border-gray-700 rounded-md"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>

        {/* Main Menu Container */}
        <div
          className={`${
            menuOpen ? "flex" : "hidden"
          } lg:flex flex-col lg:flex-row lg:items-center absolute lg:static top-full left-0 w-full lg:w-auto bg-white dark:bg-[#12151f] border-t lg:border-0 border-gray-200 dark:border-gray-800 shadow-md lg:shadow-none`}
        >
          {/* Left: Nav Links + Region */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-6 px-4 pt-4 lg:p-0">
            {/* Nav Links */}
            {navLinks.map((link, i) => (
              <motion.div
                key={link.to}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: i * 0.06 }}
              >
                <NavLink
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-base font-medium rounded-md transition-colors text-left lg:text-center ${
                      isActive
                        ? "text-blue-500 font-semibold"
                        : "text-gray-700 dark:text-gray-300 hover:text-blue-500"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </motion.div>
            ))}

            {/* REGION SELECTOR */}
            <div className="relative flex flex-col mt-4 mb-6 lg:mt-0 lg:mb-0">
              {/* Region Button - same on mobile & desktop */}
              <button
                onClick={() => setIsRegionOpen(!isRegionOpen)}
                className="flex items-center  w-auto gap-2 !px-4 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-full bg-gray-100 dark:bg-[#1e2430] hover:bg-gray-200 dark:hover:bg-[#2a3245] transition-colors"
              >
                {selectedRegion?.image ? (
                  <img
                    src={selectedRegion.image}
                    alt=""
                    className="w-5 h-5 rounded-full object-cover"
                  />
                ) : (
                  <FaFlag className="w-4 h-4" />
                )}
                <span className="mr-4 md:mr-0">
                  {selectedRegion?.name || "Region"}
                </span>
              </button>

              {/* MOBILE: Inline scrollable dropdown */}
              {isRegionOpen && (
                <div className="lg:hidden w-full mt-3 max-h-64 overflow-y-auto bg-white dark:bg-[#1e2430] border-t border-gray-200 dark:border-gray-700">
                  {regions.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleRegionSelect(r)}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {r.image && (
                        <img
                          src={r.image}
                          alt={r.name}
                          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <span
                        className={
                          selectedRegion?.id === r.id ? "font-medium" : ""
                        }
                      >
                        {r.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* DESKTOP: Absolute dropdown under button */}
              {isRegionOpen && (
                <div className="hidden lg:block  absolute top-full left-0 mt-2 w-48 bg-white dark:bg-[#1e2430] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                  {regions.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleRegionSelect(r)}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
                    >
                      {r.image && (
                        <img
                          src={r.image}
                          alt={r.name}
                          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <span
                        className={
                          selectedRegion?.id === r.id ? "font-medium" : ""
                        }
                      >
                        {r.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-4 px-4 py-4 lg:py-0 border-t lg:border-0 border-gray-200 dark:border-gray-800">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-[#1e2430]"
              title={isDark ? "Light mode" : "Dark mode"}
            >
              {isDark ? <FaSun /> : <FaMoon />}
            </button>

            <button
              onClick={() => {
                setIsSubscribeModal(true);
                setMenuOpen(false);
              }}
              className="px-5 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition-colors"
            >
              Subscribe
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
